import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageShell, { Breadcrumb } from "@/components/feature/PageShell";

export default function NewsConference() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleNav = (href: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(href);
  };

  const agendaIcons = [
    "ri-vip-crown-line", "ri-mic-line", "ri-cup-line", "ri-chat-3-line",
    "ri-restaurant-line", "ri-stack-line", "ri-cup-line", "ri-award-line",
    "ri-flag-line", "ri-star-line",
  ];

  const speakerIcons = Array(6).fill("ri-user-3-line");

  return (
    <PageShell>
      <section className="relative pt-24 pb-24 bg-gradient-to-b from-accent-800 to-accent-700 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Grand%20conference%20hall%20with%20elegant%20stage%20lighting%20and%20curved%20architecture%2C%20golden%20warm%20illumination%2C%20prestigious%20international%20event%20atmosphere%2C%20luxurious%20modern%20design%2C%20no%20text%2C%20no%20people&width=1600&height=700&seq=conference-hero-02&orientation=landscape')] bg-cover bg-center opacity-25"></div>
        <div className="relative w-full max-w-7xl mx-auto px-4 md:px-8">
          <Breadcrumb items={[{ label: t("nav.news") }, { label: t("news.conference.title") }]} />
          <div className="mt-6 max-w-3xl">
            <div className="inline-block bg-accent-400/30 text-background-50 text-sm font-medium px-4 py-1.5 rounded-full mb-4 whitespace-nowrap">
              {t("news.conference.date")}
            </div>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 leading-tight">
              {t("news.conference.title")}
            </h1>
            <p className="mt-4 text-lg text-background-100/80 leading-relaxed">
              {t("news.conference.subtitle")}
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <a href="/contact" onClick={handleNav("/contact")} className="bg-background-50 text-accent-800 px-6 py-3 rounded-md text-sm font-semibold hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                {t("news.conference.register")}
              </a>
              <a href="/about/introduction" onClick={handleNav("/about/introduction")} className="border border-background-100/40 text-background-50 px-6 py-3 rounded-md text-sm font-medium hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap">
                {t("news.conference.learnMore")}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Conference Info */}
      <section className="py-16 md:py-20">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: "ri-calendar-line", labelKey: "news.conference.info.date", valueKey: "news.conference.info.dateVal", color: "primary" },
              { icon: "ri-map-pin-line", labelKey: "news.conference.info.location", valueKey: "news.conference.info.locationVal", color: "accent" },
              { icon: "ri-team-line", labelKey: "news.conference.info.scale", valueKey: "news.conference.info.scaleVal", color: "secondary" },
              { icon: "ri-global-line", labelKey: "news.conference.info.countries", valueKey: "news.conference.info.countriesVal", color: "primary" },
            ].map((item, i) => (
              <div key={i} className="bg-background-100 rounded-lg p-6 text-center">
                <div className={`w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-${item.color}-100 text-${item.color}-700 mb-3`}>
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">{t(item.labelKey)}</p>
                <p className="font-heading font-bold text-foreground-900 text-lg">{t(item.valueKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Agenda */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-2">{t("news.conference.agenda")}</h2>
          <p className="text-foreground-500 mb-8">{t("news.conference.agenda.desc")}</p>
          <div className="space-y-3">
            {Array.from({ length: 10 }, (_, i) => (
              <div key={i} className="flex items-start gap-4 bg-background-50 rounded-lg p-5">
                <div className="flex-shrink-0 w-28 text-sm font-medium text-primary-700 pt-0.5 whitespace-nowrap">{t(`news.conference.agenda.${i}.time`)}</div>
                <div className="w-px h-full min-h-[40px] bg-background-300 flex-shrink-0 self-stretch"></div>
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className={agendaIcons[i]}></i>
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-foreground-900">{t(`news.conference.agenda.${i}.title`)}</h3>
                  <p className="text-sm text-foreground-600 mt-0.5">{t(`news.conference.agenda.${i}.desc`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Speakers */}
      <section className="py-16 md:py-20">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-2">{t("news.conference.speakers")}</h2>
          <p className="text-foreground-500 mb-8">{t("news.conference.speakers.desc")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="bg-background-100 rounded-lg p-6 flex items-center gap-4">
                <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className={speakerIcons[i]}></i>
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-foreground-900">{t(`news.conference.speaker.${i}.name`)}</h3>
                  <p className="text-xs text-foreground-500">{t(`news.conference.speaker.${i}.title`)}</p>
                  <p className="text-sm text-primary-600 mt-1 font-medium">{t(`news.conference.speaker.${i}.topic`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}